import { graphql, useFragment } from 'react-relay';

import {
  AppPushDescriptionList_appPush$data,
  AppPushDescriptionList_appPush$key,
} from '../../../relay/__generated__/AppPushDescriptionList_appPush.graphql';
import DateText from '../../core/DateText';
import DescriptionList, { DescriptionListProps } from '../../core/DescriptionList';
import EnumPair from '../../core/EnumPair';
import Text from '../../core/Text';
import AppPushStatusLabel from '../AppPushStatusLabel';

const AppPushDescriptionList_appPush = graphql`
  fragment AppPushDescriptionList_appPush on AppPush {
    id
    startAt
    pushTitle
    pushBody
    deeplink

    repeatType
    repeatValue

    created
    ...AppPushStatusLabel_appPush
  }
`;

type Props = {
  appPush: AppPushDescriptionList_appPush$key;
  type?: 'default' | 'activity';
} & Omit<DescriptionListProps<AppPushDescriptionList_appPush$data>, 'item' | 'itemDescriptions' | 'picks'>;
const AppPushDescriptionList = ({ appPush: appPushReference, type = 'default', ...props }: Props) => {
  const appPush = useFragment(AppPushDescriptionList_appPush, appPushReference);

  return (
    <DescriptionList
      item={appPush}
      itemDescriptions={{
        startAt: {
          title: '시작일',
          renderValue: ({ startAt }) => <DateText fontSize={1}>{startAt}</DateText>,
        },
        repeat: {
          title: '주기',
          renderValue: ({ repeatValue, repeatType }) => {
            const values = [
              repeatType ? <EnumPair typename={'RepeatTypeEnum'}>{repeatType}</EnumPair> : '반복 없음',
              repeatValue && repeatType ? (
                <EnumPair typename={repeatType === 'monthly' ? 'MonthDayEnum' : 'WeekdayEnum'}>{repeatValue}</EnumPair>
              ) : null,
            ].filter((v) => !!v);

            if (values.length === 0) return '-';
            return values.map((v, index) => (
              <>
                {index !== 0 ? <Text fontSize={1}>{' / '}</Text> : null}
                <Text fontSize={1}>{v}</Text>
              </>
            ));
          },
        },
        pushTitle: {
          title: '제목',
          renderValue: ({ pushTitle }) => (
            <Text fontSize={1} whiteSpace={'pre-wrap'}>
              {pushTitle}
            </Text>
          ),
        },
        pushBody: {
          title: '본문',
          renderValue: ({ pushBody }) => (
            <Text fontSize={1} whiteSpace={'pre-wrap'}>
              {pushBody}
            </Text>
          ),
        },
        deeplink: {
          title: '딥링크',
          renderValue: ({ deeplink }) => {
            return deeplink ? <Text fontSize={1}>{deeplink}</Text> : null;
          },
        },
        created: {
          title: '제작일',
          renderValue: ({ created }) => <DateText fontSize={1}>{created}</DateText>,
        },
        status: {
          title: '상태',
          renderValue: (appPush) => <AppPushStatusLabel appPush={appPush} />,
        },
      }}
      picks={
        type === 'default'
          ? ['startAt', 'repeat', 'pushTitle', 'pushBody', 'deeplink']
          : type === 'activity'
          ? ['created', 'status']
          : []
      }
      {...props}
    />
  );
};

export default AppPushDescriptionList;
